import { BrandFamilyLine } from "@/components/brand-family";
import { BrandLogo } from "@/components/brand-logo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, type ReactNode } from "react";
import { ArrowLeft } from "lucide-react";

import { JsonLd } from "@/components/seo/json-ld";
import { breadcrumbSchema, seoHead } from "@/lib/seo";

export const Route = createFileRoute("/terms/")({
  ssr: true,
  head: () =>
    seoHead({
      title: "Terms of Service",
      description:
        "Terms and conditions governing use of the Bavimail email infrastructure platform.",
      path: "/terms/",
      // Legal-only boilerplate page; no search-traffic value. Excluding from
      // index frees crawl budget for the rest of the property.
      noindex: true,
    }),
  component: TermsPage,
});

/* ─── Bullet list helper ───────────────────────────────── */

function Bl({ children }: { children: ReactNode }) {
  return <ul className="space-y-2.5 mt-3 ml-0.5">{children}</ul>;
}

function Li({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="w-1 h-1 rounded-full bg-[#f6821f] mt-[0.65em] shrink-0" />
      <span>{children}</span>
    </li>
  );
}

/* ─── Section data ─────────────────────────────────────── */

const sections: { id: string; num: string; title: string; content: ReactNode }[] = [
  {
    id: "agreement",
    num: "01",
    title: "Agreement to Terms",
    content: (
      <>
        <p>
          These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the Bavimail platform,
          including our website, dashboard, APIs, SDKs, and related services (collectively, the
          &ldquo;Service&rdquo;). By accessing or using the Service, you agree to be bound by these
          Terms.
        </p>
        <p>
          If you are using the Service on behalf of an organization, you represent that you have the
          authority to bind that organization to these Terms. &ldquo;You&rdquo; refers to both you
          as an individual and the organization you represent.
        </p>
        <p>If you do not agree to these Terms, do not use the Service.</p>
      </>
    ),
  },
  {
    id: "accounts",
    num: "02",
    title: "Account Registration",
    content: (
      <>
        <p>To use Bavimail, you must create an account. When registering, you agree to:</p>
        <Bl>
          <Li>Be at least 16 years old</Li>
          <Li>Provide accurate and complete information</Li>
          <Li>Keep your credentials (password and API keys) secure and confidential</Li>
          <Li>Notify us immediately of any unauthorized access to your account</Li>
          <Li>Accept responsibility for all activity that occurs under your account</Li>
        </Bl>
        <p>
          We may suspend or terminate accounts that contain false information or are used in
          violation of these Terms.
        </p>
      </>
    ),
  },
  {
    id: "service",
    num: "03",
    title: "Service Description",
    content: (
      <>
        <p>Bavimail is an email infrastructure platform. The Service includes:</p>
        <Bl>
          <Li>Transactional and marketing email delivery via API and web dashboard</Li>
          <Li>Domain authentication and DNS management (SPF, DKIM, DMARC)</Li>
          <Li>Inbound email processing, parsing, and webhook routing</Li>
          <Li>Email engagement tracking (open and click analytics)</Li>
          <Li>Webhook event delivery for real-time notifications</Li>
          <Li>Deliverability analytics and reputation monitoring</Li>
          <Li>Suppression list management and automatic bounce processing</Li>
          <Li>Official SDKs for TypeScript and Python</Li>
          <Li>REST API with full programmatic access</Li>
        </Bl>
        <p>
          We may add, modify, or discontinue features of the Service at any time. We will provide
          reasonable notice for material changes that affect your use of the platform.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    num: "04",
    title: "Acceptable Use",
    content: (
      <>
        <p>
          You agree to use the Service only for lawful purposes and in compliance with these Terms.
          You agree not to:
        </p>
        <Bl>
          <Li>
            Send unsolicited bulk email (spam) or messages to recipients who have not consented
          </Li>
          <Li>Use purchased, rented, scraped, or harvested email lists</Li>
          <Li>Send content that is fraudulent, deceptive, defamatory, or illegal</Li>
          <Li>Distribute malware, phishing attempts, or harmful content</Li>
          <Li>Impersonate another person or entity, or use misleading sender information</Li>
          <Li>Attempt to circumvent rate limits, usage quotas, or security measures</Li>
          <Li>Interfere with or disrupt the Service or its infrastructure</Li>
          <Li>Reverse engineer, decompile, or attempt to extract the source code of the Service</Li>
          <Li>Use the Service in any way that violates applicable laws or regulations</Li>
          <Li>Resell or redistribute the Service without written permission</Li>
        </Bl>
        <p>
          We reserve the right to investigate and take action against any violations, including
          suspending or terminating your account.
        </p>
      </>
    ),
  },
  {
    id: "email-policies",
    num: "05",
    title: "Email Sending Policies",
    content: (
      <>
        <p>
          All email sent through Bavimail must comply with applicable laws, including but not
          limited to CAN-SPAM (United States), CASL (Canada), GDPR (European Union), and other
          anti-spam legislation. Specifically, you must:
        </p>
        <Bl>
          <Li>Have valid consent or a legitimate basis to email each recipient</Li>
          <Li>Include a functioning unsubscribe mechanism in marketing emails</Li>
          <Li>Honor unsubscribe and opt-out requests promptly</Li>
          <Li>Use accurate sender information and not forge email headers</Li>
          <Li>Authenticate your sending domains with SPF, DKIM, and DMARC records</Li>
          <Li>Maintain clean recipient lists and remove invalid addresses</Li>
        </Bl>
        <p>
          Bavimail automatically processes bounces, complaints, and unsubscribes. Suppression lists
          are updated in real-time. Sending to a suppressed address will be blocked automatically.
        </p>
        <p>
          We monitor sending patterns and may suspend your sending privileges if we detect abnormal
          bounce rates, complaint rates, or patterns indicative of spam.
        </p>
        <p>
          <strong>Fair use.</strong> Your outbound volume, including the size and number of
          attachments, must stay consistent with ordinary use of your plan. Outbound data transfer
          is not unlimited: if your account sends an unusually large volume of messages or
          attachment data, or its outbound data volume imposes disproportionate cost on the
          platform, we may throttle or pause your sending, ask you to move to a plan or agreement
          that covers that volume, or charge for the excess data transfer at the rates we disclose
          to you in advance.
        </p>
      </>
    ),
  },
  {
    id: "api-usage",
    num: "06",
    title: "API & SDK Usage",
    content: (
      <>
        <p>
          Your use of the Bavimail API and SDKs is subject to these Terms and any applicable API
          documentation.
        </p>
        <Bl>
          <Li>
            API keys are confidential. You must not share, publish, or embed them in client-side
            code or public repositories
          </Li>
          <Li>
            Rate limits apply to all API calls based on your plan. Exceeding limits may result in
            throttled or rejected requests
          </Li>
          <Li>We may revoke API keys if we detect misuse, abuse, or a security breach</Li>
          <Li>
            The API is provided on a commercially reasonable basis. We aim for high availability but
            do not guarantee uninterrupted access
          </Li>
          <Li>Our SDKs are open source and distributed under their respective licenses</Li>
        </Bl>
        <p>
          You are responsible for securing your integration and ensuring that your use of the API
          does not compromise the security or performance of the platform.
        </p>
      </>
    ),
  },
  {
    id: "billing",
    num: "07",
    title: "Pricing & Payment",
    content: (
      <>
        <p>
          Bavimail offers tiered pricing plans: Free, Pro, Startup, and Enterprise. Full pricing
          details are available on our{" "}
          <Link to="/" hash="pricing" className="text-[#f6821f] hover:underline">
            pricing page
          </Link>
          .
        </p>
        <Bl>
          <Li>
            The Free tier includes a fixed monthly email allocation with no overage charges. Usage
            is hard-capped at the plan limit
          </Li>
          <Li>
            Paid plans include a base monthly or annual fee plus usage-based overage charges for
            emails and storage beyond the included allocation
          </Li>
          <Li>
            All payments are processed by Stripe. By subscribing to a paid plan, you authorize
            Stripe to charge your payment method on a recurring basis
          </Li>
          <Li>
            Subscription fees are non-refundable. If you cancel mid-period, your access continues
            until the end of the current billing cycle
          </Li>
          <Li>
            We may change pricing with at least 30 days written notice. Price changes take effect at
            the start of your next billing cycle
          </Li>
        </Bl>
        <p>
          Enterprise plans are subject to separate agreements. Contact{" "}
          <a href="mailto:support@bavimail.com" className="text-[#f6821f] hover:underline">
            support@bavimail.com
          </a>{" "}
          for details.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    num: "08",
    title: "Intellectual Property",
    content: (
      <>
        <p>
          <strong>Our property.</strong> The Bavimail platform, including its software, design,
          branding, documentation, and all related intellectual property, is owned by Bavimail and
          protected by applicable copyright, trademark, and other laws. These Terms do not grant you
          any rights to our intellectual property except the limited right to use the Service as
          described herein.
        </p>
        <p>
          <strong>Your content.</strong> You retain all ownership rights to the content you send,
          receive, and store through Bavimail, including email messages, attachments, domain
          configurations, and templates. You grant us a limited license to process, store, and
          transmit your content solely as necessary to provide the Service.
        </p>
        <p>
          <strong>Feedback.</strong> If you provide feedback, suggestions, or ideas about the
          Service, we may use them without obligation to you.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    num: "09",
    title: "Privacy",
    content: (
      <>
        <p>
          Your use of the Service is also governed by our{" "}
          <Link to="/privacy" className="text-[#f6821f] hover:underline">
            Privacy Policy
          </Link>
          , which describes how we collect, use, and protect your information. By using the Service,
          you agree to the data practices described in the Privacy Policy.
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    num: "10",
    title: "Disclaimers & Limitations",
    content: (
      <>
        <p>
          <strong>As-is service.</strong> The Service is provided &ldquo;as is&rdquo; and &ldquo;as
          available&rdquo; without warranties of any kind, whether express, implied, or statutory.
          We disclaim all warranties, including but not limited to warranties of merchantability,
          fitness for a particular purpose, and non-infringement.
        </p>
        <p>
          <strong>No delivery guarantee.</strong> While we strive for high deliverability, we cannot
          guarantee that every email will be delivered to every recipient&rsquo;s inbox. Email
          delivery depends on factors outside our control, including recipient server
          configurations, spam filters, and domain reputation.
        </p>
        <p>
          <strong>Limitation of liability.</strong> To the maximum extent permitted by law,
          Bavimail&rsquo;s total liability for any claims arising from or related to the Service is
          limited to the amount you paid us in the 12 months preceding the claim. We are not liable
          for any indirect, incidental, special, consequential, or punitive damages, including lost
          profits, data loss, or business interruption.
        </p>
      </>
    ),
  },
  {
    id: "indemnification",
    num: "11",
    title: "Indemnification",
    content: (
      <>
        <p>
          You agree to indemnify, defend, and hold harmless Bavimail and its officers, directors,
          employees, and agents from and against any claims, liabilities, damages, losses, and
          expenses (including reasonable attorneys&rsquo; fees) arising from:
        </p>
        <Bl>
          <Li>Your use of the Service</Li>
          <Li>Your violation of these Terms</Li>
          <Li>Your violation of any applicable law or regulation</Li>
          <Li>The content you send, receive, or store through the Service</Li>
          <Li>Any third-party claims related to emails you send using Bavimail</Li>
        </Bl>
      </>
    ),
  },
  {
    id: "termination",
    num: "12",
    title: "Termination",
    content: (
      <>
        <p>
          <strong>By you.</strong> You may stop using the Service and delete your account at any
          time through the dashboard settings. Deletion is effective immediately, subject to our
          data retention policy.
        </p>
        <p>
          <strong>By us.</strong> We may suspend or terminate your access if you violate these
          Terms, engage in abusive behavior, or fail to pay applicable fees. We may also terminate
          accounts with 30 days written notice for any reason.
        </p>
        <p>
          <strong>Effect of termination.</strong> Upon termination, your right to use the Service
          ends immediately. Your data will be deleted according to our retention policy. Sections of
          these Terms that should survive termination &mdash; including intellectual property,
          limitation of liability, indemnification, and governing law &mdash; will continue to
          apply.
        </p>
      </>
    ),
  },
  {
    id: "modifications",
    num: "13",
    title: "Modifications",
    content: (
      <>
        <p>
          We may modify these Terms at any time. When we make material changes, we will notify you
          via email or a notice in the dashboard at least 30 days before the changes take effect.
        </p>
        <p>
          Your continued use of the Service after changes become effective constitutes acceptance of
          the modified Terms. If you disagree with any changes, you should stop using the Service
          and delete your account.
        </p>
        <p>
          The &ldquo;Last updated&rdquo; date at the top of this page indicates when these Terms
          were most recently revised.
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    num: "14",
    title: "Governing Law",
    content: (
      <>
        <p>
          These Terms are governed by and construed in accordance with applicable law, without
          regard to conflict of law principles. Any disputes arising from these Terms or the Service
          will be resolved through binding arbitration or in the courts of competent jurisdiction.
        </p>
        <p>
          If any provision of these Terms is found to be unenforceable, the remaining provisions
          will continue in full force and effect.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    num: "15",
    title: "Contact",
    content: (
      <>
        <p>If you have questions about these Terms of Service, contact us:</p>
        <div className="mt-4 px-5 py-4 border border-dashed border-[#2a2a2e] bg-[#0a0a0c] inline-block">
          <div className="text-[#d0d0d0] font-semibold font-hb mb-1">Bavimail</div>
          <a href="mailto:support@bavimail.com" className="text-[#f6821f] hover:underline">
            support@bavimail.com
          </a>
        </div>
      </>
    ),
  },
];

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

function TermsPage() {
  const [scrollPct, setScrollPct] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollPct(docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Terms of Service", path: "/terms/" },
        ])}
      />
      <style>{`
        .font-hb { font-family: 'Manrope', system-ui, sans-serif; }
        .font-mono-hb { font-family: 'Chivo Mono', monospace; }
        .bg-grid-hb {
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .hb-accent-bar {
          background: linear-gradient(90deg, #f6821f, #ff6633, #f6821f);
          background-size: 200% 100%;
        }
      `}</style>

      <div className="font-hb min-h-screen bg-[#0d0d0f] text-[#d0d0d0] relative selection:bg-[#f6821f] selection:text-black flex flex-col">
        <div className="fixed inset-0 bg-grid-hb pointer-events-none" />
        <div
          className="h-[3px] fixed top-0 left-0 z-[60] hb-accent-bar"
          style={{
            width: `${scrollPct * 100}%`,
            transition: "width 0.15s ease-out",
          }}
        />

        {/* ── Header ──────────────────────────────────── */}
        <header className="sticky top-[3px] z-50 border-b border-[#1a1a1e] bg-[#0d0d0f]/80 backdrop-blur-md">
          <div className="max-w-4xl mx-auto flex items-center justify-between px-6 md:px-8 py-4">
            <Link
              to="/"
              aria-label="Bavimail home"
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <BrandLogo className="h-6 w-auto" />
            </Link>
            <Link
              to="/"
              className="group flex items-center gap-2 font-mono-hb text-[11px] text-[#7a7a7a] hover:text-[#f6821f] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
              Back to home
            </Link>
          </div>
        </header>

        {/* ── Content ─────────────────────────────────── */}
        <main className="relative z-10 flex-grow max-w-4xl mx-auto w-full px-6 md:px-8 py-12 md:py-16 lg:py-20">
          {/* Title block */}
          <div className="mb-14">
            <div className="font-mono-hb text-[10px] uppercase tracking-[0.2em] text-[#7a7a7a] mb-4">
              Legal
            </div>
            <h1 className="font-hb font-extrabold text-3xl md:text-[2.75rem] text-white tracking-tight leading-tight mb-4">
              Terms of Service
            </h1>
            <div className="flex flex-wrap items-center gap-3 font-mono-hb text-[11px] text-[#7a7a7a]">
              <span>Effective March 15, 2026</span>
              <span className="w-px h-3 bg-[#2a2a2e]" />
              <span>Last updated October 19, 2026</span>
            </div>
            <p className="mt-6 font-mono-hb text-[13px] text-[#8a8a8a] leading-[1.85] max-w-2xl">
              These Terms of Service govern your use of the Bavimail email infrastructure platform.
              By using our services, you agree to these terms.
            </p>
          </div>

          {/* Table of contents */}
          <nav className="relative border border-dashed border-[#2a2a2e] p-5 md:p-6 mb-14 bg-[#111114]">
            <div className="absolute -top-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="absolute -top-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="absolute -bottom-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="absolute -bottom-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
            <div className="font-mono-hb text-[10px] uppercase tracking-[0.2em] text-[#7a7a7a] mb-4">
              Contents
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-2.5 font-mono-hb text-[12px] text-[#8a8a8a] hover:text-[#f6821f] transition-colors py-1"
                >
                  <span className="text-[#f6821f]/40 text-[11px]">{s.num}</span>
                  <span>{s.title}</span>
                </a>
              ))}
            </div>
          </nav>

          {/* Sections */}
          <div>
            {sections.map((section, i) => (
              <section
                key={section.id}
                id={section.id}
                className={i > 0 ? "border-t border-dashed border-[#2a2a2e] pt-10 mt-10" : ""}
                style={{ scrollMarginTop: "5rem" }}
              >
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="font-mono-hb text-[11px] text-[#f6821f]">/{section.num}</span>
                  <h2 className="font-hb font-bold text-[17px] md:text-lg text-white">
                    {section.title}
                  </h2>
                </div>
                <div className="font-mono-hb text-[13px] text-[#8a8a8a] leading-[1.85] space-y-4 [&_strong]:text-[#d0d0d0] [&_strong]:font-semibold max-w-[680px]">
                  {section.content}
                </div>
              </section>
            ))}
          </div>
        </main>

        {/* ── Footer ──────────────────────────────────── */}
        <footer className="relative z-10 border-t border-[#1a1a1e] bg-[#0a0a0c] py-8 px-6 md:px-8">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-mono-hb text-[11px] text-[#6b6b6b]">
                &copy; {new Date().getFullYear()} Bavimail. All rights reserved.
              </span>
              <BrandFamilyLine
                className="font-mono-hb text-[11px] text-[#6b6b6b]"
                linkClassName="underline underline-offset-2 hover:text-[#f6821f]"
              />
            </div>
            <div className="flex items-center gap-4 font-mono-hb text-[11px] text-[#6b6b6b]">
              <Link to="/privacy" className="hover:text-[#f6821f] transition-colors">
                Privacy
              </Link>
              <span className="w-px h-3 bg-[#1a1a1e]" />
              <span className="text-[#f6821f]">Terms</span>
              <span className="w-px h-3 bg-[#1a1a1e]" />
              <Link to="/" className="hover:text-[#f6821f] transition-colors">
                Home
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
